import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Globe, Verified, Pencil, Lock } from "lucide-react";
import AppLayout from "../components/AppLayout";
import ClubFormDialog from "../components/ClubFormDialog";
import ClubMembersSection from "../components/ClubMembersSection";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import useSeoHead from "../hooks/useSeoHead";

export default function ClubDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const fetchClub = useCallback(() => {
    api.get(`/clubs/${id}`).then((r) => setC(r.data));
  }, [id]);
  useEffect(() => { fetchClub(); }, [fetchClub]);

  // SEO: SportsClub JSON-LD. Hooks must be unconditional — compute strings
  // that might be null; useSeoHead tolerates undefined values gracefully.
  const canonical = typeof window !== "undefined" && c
    ? `${window.location.origin}/clubs/${c.slug || c.id}`
    : undefined;
  const seoDescription = c
    ? `${c.name}${c.city ? ` — ${c.city}${c.country ? `, ${c.country}` : ""}` : ""}. ${c.description || "Join the club, find matches, register for tournaments and meet other players on Pick2Play."}`
    : undefined;
  const jsonLd = React.useMemo(() => {
    if (!c) return null;
    return {
      "@context": "https://schema.org",
      "@type": "SportsClub",
      name: c.name,
      url: canonical,
      image: c.logo_url || c.cover_url || undefined,
      description: c.description || seoDescription,
      address: {
        "@type": "PostalAddress",
        streetAddress: c.address || undefined,
        addressLocality: c.city || undefined,
        addressCountry: c.country || undefined,
      },
      ...(c.phone ? { telephone: c.phone } : {}),
      ...(c.email ? { email: c.email } : {}),
      ...(c.website ? { sameAs: [c.website] } : {}),
      ...(c.facilities && c.facilities.length
        ? {
          location: c.facilities.map((f) => ({
            "@type": "SportsActivityLocation",
            name: f.name,
            ...(f.city ? { address: { "@type": "PostalAddress", addressLocality: f.city, addressCountry: f.country } } : {}),
          })),
        }
        : {}),
    };
  }, [c, canonical, seoDescription]);
  useSeoHead({
    title: c ? `${c.name}${c.city ? ` — ${c.city}` : ""} · Pick2Play` : "Pick2Play",
    description: seoDescription,
    canonical,
    image: c?.logo_url || c?.cover_url,
    type: "profile",
    jsonLd,
  });

  if (!c) return <AppLayout><div className="pp-card p-10 text-center text-zinc-500">Loading...</div></AppLayout>;

  const join = async () => {
    if (!user) { navigate("/login"); return; }
    try { await api.post(`/clubs/${id}/join`); toast.success(t("joined")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // Owner / admin / federation / approved-admin-tier member can edit. We do
  // a permissive client-side check (the backend is the source of truth — it
  // will 403 if the user isn't actually allowed). Admin role is the easy
  // signal; for club_admin we check creator-id; the membership check is
  // done server-side.
  const canEdit = !!user && (
    user.role === "admin" ||
    user.role === "federation" ||
    c.created_by === user.id
  );

  return (
    <AppLayout>
      <Link to="/clubs" data-testid="back-clubs" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> {t("clubs")}</Link>

      {/* Optional cover banner */}
      {c.cover_url && (
        <div className="rounded-2xl overflow-hidden mb-4 max-h-72" data-testid="club-cover">
          <img src={c.cover_url} alt={`${c.name} cover`} className="w-full h-auto object-cover" />
        </div>
      )}

      <div className="pp-card-elevated p-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            {c.logo_url && (
              <img
                src={c.logo_url}
                alt={`${c.name} logo`}
                data-testid="club-logo"
                className="w-16 h-16 rounded-lg object-cover shrink-0 border border-white/10"
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-black" data-testid="club-name">{c.name}</h1>
                {c.is_verified && <Verified size={20} className="text-[#00E676]" />}
                {c.is_private && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFD60A]/10 text-[#FFD60A] uppercase flex items-center gap-1" data-testid="club-private-chip">
                    <Lock size={10} /> Private
                  </span>
                )}
              </div>
              <div className="mt-2 text-zinc-400 flex items-center gap-2 text-sm"><MapPin size={14} /> {[c.address, c.city, c.country].filter(Boolean).join(", ") || "—"}</div>
              <p className="text-zinc-300 mt-4 max-w-2xl">{c.description}</p>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-500">
                {c.phone && <span className="flex items-center gap-1"><Phone size={12} /> {c.phone}</span>}
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Globe size={12} /> {c.website}</a>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-start">
            <button data-testid="club-join-btn" onClick={join} className="pp-btn-primary px-6 py-3 rounded-xl">{t("join")}</button>
            {canEdit && (
              <ClubFormDialog
                mode="edit"
                club={c}
                onSaved={(fresh) => setC((prev) => ({ ...prev, ...fresh }))}
                triggerSlot={
                  <button data-testid="club-edit-btn" className="pp-btn-secondary px-5 py-3 rounded-xl flex items-center gap-1.5">
                    <Pencil size={14} /> Edit
                  </button>
                }
              />
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-heading text-xl font-bold mb-3 flex items-center gap-2">
              {t("facilities")}
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase tracking-wider">
                {c.facilities?.length || 0} venues
              </span>
            </h3>
            <div className="space-y-2" data-testid="club-facilities">
              {c.facilities?.map((f) => (
                <Link
                  key={f.id}
                  to={`/facilities/${f.slug || f.id}`}
                  data-testid={`club-facility-${f.id}`}
                  className="pp-card p-3 block hover:border-[#00E676]/40 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-medium flex items-center gap-2">
                      {f.name}
                      {f.verified && <Verified size={13} className="text-[#00E676]" />}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {(f.sport_slugs || []).slice(0, 3).map((s) => (
                        <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                    <MapPin size={11} /> {[f.city, f.country].filter(Boolean).join(", ") || f.address || "—"}
                    {f.indoor === true && <span className="ms-2">· Indoor</span>}
                    {f.indoor === false && <span className="ms-2">· Outdoor</span>}
                    {f.num_courts ? <span className="ms-2">· {f.num_courts} courts</span> : null}
                  </div>
                </Link>
              ))}
              {(c.facilities?.length || 0) === 0 && <div className="text-zinc-500 text-sm">No venues linked yet.</div>}
            </div>
          </div>
          <div>
            <ClubMembersSection clubId={id} ownerId={c.created_by} />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
